import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { scheduleApi, storesApi, visitsApi, dealsApi, calendarEventsApi, timeOffApi, calendarNotesApi } from '../services/api';
import { Calendar } from '../components/Calendar';
import { ScheduleList } from '../components/ScheduleList';
import { DayDetailDrawer } from '../components/DayDetailDrawer';
import { VisitPopup } from '../components/VisitPopup';
import { CalendarEventModal } from '../components/CalendarEventModal';
import { TimeOffModal } from '../components/TimeOffModal';
import { formatDate } from 'shared';
import type { VisitWithStore, CalendarEvent, TimeOff } from 'shared';

export function Schedule() {
  const queryClient = useQueryClient();
  const [viewMode, setViewMode] = useState<'week' | 'month' | 'list'>('week');
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedVisit, setSelectedVisit] = useState<VisitWithStore | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showTimeOffModal, setShowTimeOffModal] = useState(false);
  const [editingTimeOff, setEditingTimeOff] = useState<TimeOff | null>(null);

  const now = new Date();
  const [startDate, setStartDate] = useState(() => {
    const start = new Date(now);
    start.setDate(start.getDate() - start.getDay());
    return formatDate(start);
  });

  const endDate = (() => {
    const start = new Date(startDate);
    if (viewMode === 'week') {
      start.setDate(start.getDate() + 6);
    } else {
      start.setMonth(start.getMonth() + 1);
      start.setDate(0);
    }
    return formatDate(start);
  })();

  // Sunday of current week for notes key
  const weekNotesKey = startDate;

  const { data: visits = [], isLoading } = useQuery({
    queryKey: ['schedule', startDate, endDate],
    queryFn: () => scheduleApi.list(startDate, endDate),
  });

  const { data: stores = [] } = useQuery({
    queryKey: ['stores'],
    queryFn: () => storesApi.list(),
  });

  const { data: calendarEvents = [] } = useQuery({
    queryKey: ['calendar-events', startDate, endDate],
    queryFn: () => calendarEventsApi.list(startDate, endDate),
  });

  const { data: timeOffBlocks = [] } = useQuery({
    queryKey: ['time-off', startDate, endDate],
    queryFn: () => timeOffApi.list(startDate, endDate),
  });

  // Deals visible in this date range (active or upcoming that overlap)
  const { data: allDeals = [] } = useQuery({
    queryKey: ['deals-all'],
    queryFn: () => dealsApi.list(),
  });

  const visibleDeals = allDeals.filter(
    (d) => d.end_date >= startDate && d.start_date <= endDate
  );

  const { data: weekNote } = useQuery({
    queryKey: ['calendar-notes', weekNotesKey],
    queryFn: () => calendarNotesApi.get(weekNotesKey),
  });

  const saveNotesMutation = useMutation({
    mutationFn: (notes: string) => calendarNotesApi.save(weekNotesKey, notes),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['calendar-notes', weekNotesKey] }),
  });

  // Sync notes text when week changes
  const notesSynced = weekNote?.week_start === weekNotesKey;
  const currentNotesText = notesSynced ? weekNote?.notes ?? '' : '';

  const moveVisitMutation = useMutation({
    mutationFn: ({ visitId, toDate }: { visitId: string; toDate: string }) => {
      const visit = visits.find((v) => v.id === visitId);
      if (!visit) throw new Error('Visit not found');
      const timeStr = visit.start_datetime.split('T')[1];
      return visitsApi.update(visitId, {
        start_datetime: `${toDate}T${timeStr}`,
        duration_minutes: visit.duration_minutes,
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['schedule'] }),
  });

  const hasNoRevenue = stores.length > 0 && stores.every((s) => !s.priority?.ytd_revenue_cents);

  const generateMutation = useMutation({
    mutationFn: () => {
      const generateEnd = new Date();
      generateEnd.setDate(generateEnd.getDate() + 28);
      return scheduleApi.generate({
        start_date: formatDate(new Date()),
        end_date: formatDate(generateEnd),
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['schedule'] }),
  });

  const handlePrevious = () => {
    const start = new Date(startDate);
    if (viewMode === 'week') {
      start.setDate(start.getDate() - 7);
    } else {
      start.setMonth(start.getMonth() - 1);
    }
    setStartDate(formatDate(start));
  };

  const handleNext = () => {
    const start = new Date(startDate);
    if (viewMode === 'week') {
      start.setDate(start.getDate() + 7);
    } else {
      start.setMonth(start.getMonth() + 1);
    }
    setStartDate(formatDate(start));
  };

  const handleNotesBlur = useCallback((text: string) => {
    if (text !== currentNotesText) {
      saveNotesMutation.mutate(text);
    }
  }, [currentNotesText, saveNotesMutation]);

  const dealTypeColor = (status: string) => {
    if (status === 'active') return 'bg-green-100 text-green-800 border-green-300';
    if (status === 'upcoming') return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    return 'bg-gray-100 text-gray-600 border-gray-300';
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Schedule</h2>
        <div className="flex items-center gap-2">
          {/* Add buttons */}
          <button
            onClick={() => { setSelectedEvent(null); setShowEventModal(true); }}
            className="px-3 py-2 text-sm bg-teal-600 text-white rounded-md hover:bg-teal-700"
          >
            + Event
          </button>
          <button
            onClick={() => { setEditingTimeOff(null); setShowTimeOffModal(true); }}
            className="px-3 py-2 text-sm bg-gray-600 text-white rounded-md hover:bg-gray-700"
          >
            + Day Off
          </button>

          {/* View Toggle */}
          <div style={{ display: 'flex', borderRadius: '6px', overflow: 'hidden', border: '1px solid #d1d5db' }}>
            {(['week', 'month', 'list'] as const).map((mode) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                style={{
                  padding: '8px 12px',
                  fontSize: '14px',
                  fontWeight: 500,
                  border: 'none',
                  cursor: 'pointer',
                  backgroundColor: viewMode === mode ? '#2563eb' : '#fff',
                  color: viewMode === mode ? '#fff' : '#374151',
                }}
              >
                {mode.charAt(0).toUpperCase() + mode.slice(1)}
              </button>
            ))}
          </div>

          <button
            onClick={() => generateMutation.mutate()}
            disabled={generateMutation.isPending}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 text-sm"
          >
            {generateMutation.isPending ? 'Generating...' : 'Generate Schedule'}
          </button>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button onClick={handlePrevious} className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900">
          &larr; Previous
        </button>
        <span className="text-lg font-medium text-gray-900">
          {startDate} to {endDate}
        </span>
        <button onClick={handleNext} className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900">
          Next &rarr;
        </button>
      </div>

      {hasNoRevenue && (
        <div style={{ padding: '12px 16px', backgroundColor: '#fef3c7', borderRadius: '6px', color: '#92400e', fontSize: '14px' }}>
          No revenue data entered. Schedule generation uses default visit durations. <a href="/revenue" style={{ textDecoration: 'underline' }}>Add revenue →</a>
        </div>
      )}

      {/* Calendar/List */}
      {isLoading ? (
        <div className="text-center py-8">Loading schedule...</div>
      ) : viewMode === 'list' ? (
        <ScheduleList visits={visits} />
      ) : (
        <Calendar
          visits={visits}
          events={calendarEvents}
          timeOffBlocks={timeOffBlocks}
          startDate={startDate}
          endDate={endDate}
          viewMode={viewMode === 'month' ? 'month' : 'week'}
          onDayClick={(date) => setSelectedDate(date)}
          onVisitClick={(visit) => setSelectedVisit(visit)}
          onEventClick={(event) => { setSelectedEvent(event); setShowEventModal(true); }}
          onVisitDrop={(visitId, _fromDate, toDate) => moveVisitMutation.mutate({ visitId, toDate })}
        />
      )}

      {generateMutation.isSuccess && (
        <p className="text-green-600 text-sm">Generated {generateMutation.data?.visits_created} visits!</p>
      )}
      {generateMutation.isError && (
        <p className="text-red-600 text-sm">
          {generateMutation.error instanceof Error ? generateMutation.error.message : 'Failed to generate schedule.'}
        </p>
      )}

      {/* Bottom panel: Deals strip (left 1/4) + Week Notes (right 3/4) */}
      <div className="grid grid-cols-4 gap-4 mt-2">
        {/* Active Deals strip */}
        <div className="col-span-1 bg-white rounded-lg shadow p-3 min-h-[120px]">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Active Deals</span>
            {visibleDeals.length > 0 && (
              <span className="text-xs text-gray-400">{visibleDeals.length}</span>
            )}
          </div>
          {visibleDeals.length === 0 ? (
            <p className="text-xs text-gray-400">No active deals this week</p>
          ) : (
            <div className="space-y-1.5 overflow-y-auto max-h-40">
              {visibleDeals.map((deal) => (
                <div
                  key={deal.id}
                  className={`px-2 py-1.5 rounded border text-xs ${dealTypeColor(deal.status)}`}
                >
                  <div className="font-medium truncate">{deal.name}</div>
                  <div className="opacity-70 text-[10px]">
                    {deal.start_date} – {deal.end_date}
                  </div>
                  {deal.chain_filter && deal.chain_filter.length > 0 && (
                    <div className="opacity-70 text-[10px] truncate">{deal.chain_filter.join(', ')}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Week Notes */}
        <div className="col-span-3 bg-white rounded-lg shadow p-3 min-h-[120px] flex flex-col">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Week Notes</span>
            {saveNotesMutation.isPending && (
              <span className="text-[10px] text-gray-400">Saving...</span>
            )}
          </div>
          <textarea
            key={weekNotesKey + '-' + String(notesSynced)}
            defaultValue={currentNotesText}
            onBlur={(e) => handleNotesBlur(e.target.value)}
            placeholder="Add notes for this week..."
            className="flex-1 w-full text-sm text-gray-700 resize-none border-0 focus:outline-none focus:ring-0 placeholder-gray-300"
            style={{ minHeight: '80px' }}
          />
        </div>
      </div>

      {/* Drawers & Popups */}
      {selectedDate && !selectedVisit && (
        <DayDetailDrawer
          date={selectedDate}
          visits={visits.filter((v) => v.start_datetime.split('T')[0] === selectedDate)}
          onClose={() => setSelectedDate(null)}
        />
      )}

      {selectedVisit && (
        <VisitPopup
          visit={selectedVisit}
          onClose={() => setSelectedVisit(null)}
        />
      )}

      {showEventModal && (
        <CalendarEventModal
          event={selectedEvent}
          defaultDate={selectedDate ?? undefined}
          onClose={() => { setShowEventModal(false); setSelectedEvent(null); }}
        />
      )}

      {showTimeOffModal && (
        <TimeOffModal
          block={editingTimeOff}
          defaultDate={selectedDate ?? undefined}
          onClose={() => { setShowTimeOffModal(false); setEditingTimeOff(null); }}
        />
      )}
    </div>
  );
}
